import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { FormWrapper } from '@/form-control'
import { PasswordField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { ApiResponse, api_no_auth } from '@/services/api.service'

import AuthLayout from './_Layout'

interface IResetForm {
    password: string
    confirmPassword: string
    token: string | null
    id: string | null
}

interface IResetResponse {
    message: string
}

// Result of the on-mount token check. `activated` distinguishes a never-activated
// invitation from an activated account's reset, which drives the dead-link copy.
interface ITokenStatus {
    valid: boolean
    activated: boolean
}

type CheckState = 'checking' | 'valid' | 'invalid'

export default function ResetPasswordPage({ portal = 'family' }: { portal?: 'family' | 'provider' }) {
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')
    const id = searchParams.get('id')
    // Provider staff are a separate identity (StaffUser); their reset resolves
    // against the staff collection via the provider endpoint.
    const endpoint = portal === 'provider' ? ROUTES.auth.providerResetPassword : ROUTES.auth.resetPassword

    const [checkState, setCheckState] = useState<CheckState>('checking')
    const [activated, setActivated] = useState(true)

    // Validate the link on mount so an expired/used link shows its dead state
    // immediately, rather than after the user composes a valid password and the
    // single completePasswordReset endpoint rejects it. The status endpoint always
    // returns 200, so no 401/403 redirect can hide the answer.
    useEffect(() => {
        let cancelled = false
        async function check() {
            if (!token || !id) {
                if (!cancelled) setCheckState('invalid')
                return
            }
            const res = await api_no_auth<ITokenStatus>(
                `${ROUTES.auth.resetTokenStatus}?token=${encodeURIComponent(token)}&id=${encodeURIComponent(id)}`
            )
            if (cancelled) return
            const status = res?.data
            setActivated(status?.activated ?? true)
            setCheckState(status?.valid ? 'valid' : 'invalid')
        }
        check()
        return () => {
            cancelled = true
        }
    }, [token, id])

    if (checkState === 'checking') {
        return (
            <AuthLayout info={<></>}>
                <h1 className='mb-3 text-3xl font-medium text-center'>Reset Password</h1>
                <p className='text-center text-neutral-500'>Checking your link...</p>
            </AuthLayout>
        )
    }

    if (checkState === 'invalid') {
        // A never-activated staff invitation cannot be self-served (activation is
        // admin-controlled), so send those users to their org admin. Everyone else
        // (activated staff, family) keeps the self-service request-a-new-link path.
        const isExpiredInvitation = portal === 'provider' && !activated
        return (
            <AuthLayout info={<></>}>
                {isExpiredInvitation ? (
                    <>
                        <h1 className='mb-3 text-2xl font-medium text-center'>This invitation has expired</h1>
                        <p className='my-5 text-center text-neutral-500'>
                            For your security, invitation links expire after a short window. To get a new one,
                            ask your organization's administrator to resend your invitation from their team
                            settings.
                        </p>
                        <p className='text-sm text-center text-neutral-400'>
                            Already set up your account?{' '}
                            <Link to='/login' className='font-medium text-teal-600 hover:underline'>
                                Sign in
                            </Link>
                        </p>
                    </>
                ) : (
                    <>
                        <h1 className='mb-3 text-2xl font-medium text-center'>This link has expired</h1>
                        <p className='my-5 text-center text-neutral-500'>
                            This password reset link has expired or has already been used. Request a new one to
                            continue.
                        </p>
                        <p className='text-center'>
                            <Link
                                to='/request-reset'
                                className='font-medium text-teal-600 hover:underline'
                            >
                                Request a new reset link
                            </Link>
                        </p>
                    </>
                )}
            </AuthLayout>
        )
    }

    return (
        <>
            <AuthLayout info={<></>}>
                <h1 className='mb-3 text-3xl font-medium text-center'>Reset Password</h1>
                <h2 className='mb-5 text-center text-neutral-500'>Enter your new password below</h2>
                <FormWrapper<IResetForm>
                    endpoint={endpoint}
                    isPublic
                    id={null}
                    preventInitialLoad
                    defaultValues={{
                        password: '',
                        confirmPassword: '',
                        token: token,
                        id: id,
                    }}
                    //
                    callbackAfterSubmit={async ({ postResponse }: { postResponse?: ApiResponse<IResetResponse> }) => {
                        if (postResponse?.data?.message === 'success') {
                            window.location.replace('/login')
                        } else {
                            throw {
                                error:
                                    postResponse?.error ??
                                    'Failed to reset password. Please try again or contact us for help.',
                            }
                        }
                    }}
                    //
                    submitButtonText='Reset Password'
                    submitButtonAlignment='center'
                    validationMode='on-input'
                    //
                    validationRules={[
                        {
                            field: 'password',
                            isValid: (fieldValue) => {
                                if (import.meta.env.MODE === 'development') {
                                    return true
                                }
                                return fieldValue.length && fieldValue.length >= 8
                            },
                            prompt: 'Passwords should be at least 8 characters',
                        },
                        {
                            field: 'password',
                            isValid: (fieldValue) => {
                                if (import.meta.env.MODE === 'development') {
                                    return true
                                }
                                return /(?=.*[a-z])/.test(fieldValue ?? '')
                            },
                            prompt: 'Passwords should contain a lowercase letter',
                        },
                        {
                            field: 'password',
                            isValid: (fieldValue) => {
                                if (import.meta.env.MODE === 'development') {
                                    return true
                                }
                                return /(?=.*[A-Z])/.test(fieldValue ?? '')
                            },
                            prompt: 'Passwords should contain an uppercase letter',
                        },
                        {
                            field: 'password',
                            isValid: (fieldValue) => {
                                if (import.meta.env.MODE === 'development') {
                                    return true
                                }
                                return /(?=.*\d)/.test(fieldValue ?? '')
                            },
                            prompt: 'Passwords should contain a number',
                        },
                        {
                            field: 'password',
                            isValid: (fieldValue) => {
                                if (import.meta.env.MODE === 'development') {
                                    return true
                                }
                                return /(?=.*\W)/.test(fieldValue ?? '')
                            },
                            prompt: 'Passwords should contain a special character',
                        },
                        {
                            field: 'confirmPassword',
                            isValid: (fieldValue, formValues) => {
                                return fieldValue === formValues?.password
                            },
                            prompt: 'Passwords do not match',
                        },
                    ]}
                >
                    {(f) => (
                        <>
                            <PasswordField {...f('password')} autoComplete='new-password' name='password' />
                            <PasswordField
                                {...f('confirmPassword')}
                                autoComplete='new-password'
                                name='confirmPassword'
                            />
                        </>
                    )}
                </FormWrapper>
                {/* Recovery path: reset/activation links expire, so always offer a way
                    to get a fresh one if this page reports the link is no longer valid. */}
                <p className='mt-5 text-sm text-center text-neutral-500'>
                    Link expired or not working?{' '}
                    <Link to='/request-reset' className='font-medium text-teal-600 hover:underline'>
                        Request a new one
                    </Link>
                </p>
            </AuthLayout>
        </>
    )
}
